import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { getNotifications, markAsRead } from '@/api/notifications'

export function AnnouncementModal() {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const { data } = useQuery({
        queryKey: ['notifications', 'announcements'],
        queryFn: () => getNotifications({ ordering: '-created_at' }),
        // We can poll or just fetch once on mount/auth
        staleTime: 5 * 60 * 1000,
    })

    const markReadMutation = useMutation({
        mutationFn: markAsRead,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] })
        },
    })

    // Find the first unread announcement
    const announcement = data?.results.find(
        (n) => n.notification_type === 'ANNOUNCEMENT' && !n.is_read
    )

    const isOpen = !!announcement

    const handleMarkAsRead = () => {
        if (announcement) {
            markReadMutation.mutate(announcement.id)
        }
    }

    if (!announcement) return null

    return (
        <Dialog open={isOpen} onOpenChange={() => { }}>
            <DialogContent className="sm:max-w-2xl [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
                <DialogHeader>
                    <DialogTitle className="text-2xl font-bold text-center border-b pb-4 mb-4">
                        {announcement.title}
                    </DialogTitle>
                </DialogHeader>
                <ScrollArea className="h-[60vh] px-4">
                    <div className="prose prose-sm dark:prose-invert max-w-none text-start prose-headings:font-sans prose-p:font-sans" dir={t('dir', { defaultValue: 'auto' })}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {announcement.message}
                        </ReactMarkdown>
                    </div>
                </ScrollArea>
                <DialogFooter className="mt-6 sm:justify-center border-t pt-4">
                    <Button
                        onClick={handleMarkAsRead}
                        size="lg"
                        className="w-full sm:w-auto min-w-[200px]"
                        disabled={markReadMutation.isPending}
                    >
                        {markReadMutation.isPending ? (
                            t('processing', { defaultValue: 'Processing...' })
                        ) : (
                            <>
                                <Check className="mr-2 h-5 w-5" />
                                {t('mark_as_read_and_continue', { defaultValue: 'I have read and understood' })}
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
